import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import API from "../lib/api";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { ShieldCheck, Warning, XCircle, CheckCircle, ArrowClockwise, CaretDown, CaretUp, BellRinging } from "@phosphor-icons/react";
import { toast } from "sonner";

const ICON = {
  ok: <CheckCircle size={13} weight="fill" className="text-emerald-600 shrink-0" />,
  warn: <Warning size={13} weight="fill" className="text-amber-500 shrink-0" />,
  fail: <XCircle size={13} weight="fill" className="text-red-600 shrink-0" />,
};
const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "never");

function AlertsRow({ alerts, onToggle, onRun, busy }) {
  return (
    <div className="border-t border-border/60 px-4 py-2 flex flex-wrap items-center gap-3 text-xs" data-testid="deploy-health-alerts">
      <label className="flex items-center gap-2 cursor-pointer">
        <Switch checked={alerts.enabled !== false} disabled={busy} onCheckedChange={onToggle} data-testid="deploy-health-alerts-enabled" />
        <BellRinging size={13} className="text-muted-foreground" /> Email platform admins when a check fails
      </label>
      <span className="text-muted-foreground">checked every 5 min · one email per check per 24 h · recovery note when it clears</span>
      <Button size="sm" variant="ghost" className="h-6 text-xs px-2" onClick={onRun} disabled={busy} data-testid="deploy-health-alerts-run">Check now</Button>
      <span className="text-[10px] text-muted-foreground" data-testid="deploy-health-alerts-last">Last alert: {fmtWhen(alerts.last_alert_at)}</span>
      {alerts.recent?.length > 0 && (
        <ul className="basis-full text-[10px] text-muted-foreground space-y-0.5" data-testid="deploy-health-alerts-recent">
          {alerts.recent.slice(0, 4).map((r, i) => (
            <li key={`${r.at}-${r.key}-${i}`} data-testid={`deploy-health-alert-${r.key}`}>
              {fmtWhen(r.at)} · <span className={r.to === "fail" ? "text-red-700 font-medium" : "text-emerald-700 font-medium"}>{r.label} → {r.to}</span>{r.emailed ? " · emailed" : ""}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function DeploymentHealthCard() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    API.get("/platform/health-report").then(({ data }) => { setReport(data); setOpen(data.status !== "ok" || window.location.hash === "#deploy-health"); }).catch(() => setReport(null)).finally(() => setLoading(false));
  };
  useEffect(load, []);
  const { hash } = useLocation();
  useEffect(() => {
    if (hash === "#deploy-health") { setOpen(true); setTimeout(() => document.getElementById("deploy-health")?.scrollIntoView({ behavior: "smooth", block: "start" }), 100); }
  }, [hash]);

  const toggleAlerts = async (v) => {
    setBusy(true);
    try {
      const { data } = await API.put("/platform/health-alerts", { enabled: v });
      setReport((r) => ({ ...r, alerts: { ...(r?.alerts || {}), enabled: data.enabled } }));
      toast.success(data.enabled ? "Failure alerts on — admins get an email + notification when a check fails" : "Failure alerts off");
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't save"); } finally { setBusy(false); }
  };

  const runAlerts = async () => {
    setBusy(true);
    try {
      const { data } = await API.post("/platform/health-alerts/run");
      toast.success(data.emailed?.length ? `Alerted ${data.recipients} admin(s): ${data.emailed.join(", ")}` : data.failed?.length ? `Failing (already alerted in the last 24 h): ${data.failed.join(", ")}` : "No new failures — nothing to alert");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Check failed"); } finally { setBusy(false); }
  };

  if (loading && !report) return <div className="mb-6 rounded-lg border border-border px-4 py-3 text-xs text-muted-foreground" data-testid="deploy-health-loading">Checking deployment health…</div>;
  if (!report) return null;
  const s = report.summary || {};
  const tone = report.status === "ok" ? "border-emerald-200 bg-emerald-50/60 dark:bg-emerald-950/20" : report.status === "warn" ? "border-amber-300 bg-amber-50/60 dark:bg-amber-950/20" : "border-red-300 bg-red-50/60 dark:bg-red-950/20";

  return (
    <div id="deploy-health" className={`mb-6 rounded-lg border ${tone}`} data-testid="deploy-health-card">
      <div role="button" tabIndex={0} onClick={() => setOpen((v) => !v)} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpen((v) => !v); } }} className="w-full flex items-center justify-between px-4 py-3 text-left cursor-pointer select-none" data-testid="deploy-health-toggle">
        <div className="flex items-center gap-2 text-sm font-semibold">
          <ShieldCheck size={16} weight="fill" className={report.status === "ok" ? "text-emerald-600" : report.status === "warn" ? "text-amber-500" : "text-red-600"} />
          Deployment health
          <Badge variant="outline" className="text-[10px] ml-1" data-testid="deploy-health-summary">{s.ok} ok · {s.warn} warning{s.warn === 1 ? "" : "s"} · {s.fail} failure{s.fail === 1 ? "" : "s"}</Badge>
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span className="hidden sm:inline">{report.instance}</span>
          <Button size="sm" variant="ghost" className="h-6 px-2" onClick={(e) => { e.stopPropagation(); load(); }} disabled={loading} data-testid="deploy-health-refresh"><ArrowClockwise size={12} /></Button>
          {open ? <CaretUp size={12} /> : <CaretDown size={12} />}
        </div>
      </div>
      {open && (
        <>
          <ul className="border-t border-border/60 divide-y divide-border/40" data-testid="deploy-health-checks">
            {report.checks.map((c) => (
              <li key={c.key} className="flex items-start gap-2 px-4 py-1.5 text-xs" data-testid={`deploy-health-${c.key}`}>
                {ICON[c.status] || ICON.warn}
                <span className="font-medium w-44 shrink-0">{c.label}</span>
                <span className="text-muted-foreground">{c.detail}</span>
              </li>
            ))}
          </ul>
          <AlertsRow alerts={report.alerts || {}} onToggle={toggleAlerts} onRun={runAlerts} busy={busy} />
        </>
      )}
    </div>
  );
}
